import m, {type Children, MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const columnMeta = {
    tier: 'layout',
    summary: 'Reading-width column for prose (`Heading`, `Paragraph`, `List`, `Quote`), with an optional `aside` rail beside it.',
    when_to_use:
        'Constrains the measure while the text components own their typography. Use `type="narrow"` for legal or privacy long-form. Fill `aside` with content that belongs beside the prose rather than in it (e.g. a `Well` holding a check `List` and a `Button`, a `Card`, a `QuoteCard`) — it fills the space left of the reading measure instead of leaving it empty, rides along while the prose scrolls, and drops below the prose on narrow viewports.',
    example: `
    <Column type="narrow">
        <Heading level={2}>Privacy statement</Heading>
        <Paragraph>We only process what an order actually needs.</Paragraph>
    </Column>
    `,
    variants: [
        {
            prop: 'aside',
            jsx: `
            <Column aside={<QuoteCard><Paragraph>Sourcing took days, not weeks.</Paragraph></QuoteCard>}>
                <Heading level={2}>How sourcing works</Heading>
                <Paragraph>Tell us what you need and we go looking for it.</Paragraph>
            </Column>
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface ColumnAttrs {
    /**
     * Sidebar rail beside the prose on wide viewports — e.g. a `Well` with a check `List` and a
     * `Button`. It sticks while the prose scrolls and stacks below it on narrow viewports.
     */
    aside?: Children
    /** `narrow` — tighter reading measure for legal/privacy long-form. */
    type?: 'narrow'
}

/**
 * Reading-width column for prose (`Heading`, `Paragraph`, `List`, `Quote`). Replaces the old
 * ViewProse wrapper — it constrains width; the text components own their typography. An `aside`
 * turns it into prose plus a sidebar rail, so a long page does not leave the space beside the
 * reading measure empty.
 */
export class Column extends MithrilComponent<ColumnAttrs> {
    view(vnode: m.Vnode<ColumnAttrs>) {
        const {aside, type} = vnode.attrs
        const class_name = classes('cms-column', type === 'narrow' && 'cms-column-narrow', aside && 'cms-column-with-aside')

        if (!aside) {
            return <div className={class_name}>{vnode.children}</div>
        }

        return (
            <div className={class_name}>
                <div className='body'>{vnode.children}</div>
                <aside className='aside'>{aside}</aside>
            </div>
        )
    }
}
